import { Link } from "react-router-dom";
import { ArrowRight, Hammer, Truck, Pickaxe, Trees, Droplets, LayoutGrid, Route } from "lucide-react";
import HeroCarousel from "../components/HeroCarousel";
import { HomeTestimonials } from "../components/testimonials/HomeTestimonials";
import { FeaturedTransformation } from "../components/home/FeaturedTransformation";
import { usePageTitle } from "../hooks/usePageTitle";

const ICONS = { Pickaxe, Hammer, Truck, LayoutGrid, Trees, Droplets, Route };

const SERVICES = [
  { slug: "excavation",  title: "Excavation", icon: "Pickaxe", blurb: "Site digs, foundation excavation, trenching, demolition prep." },
  { slug: "concrete",    title: "Concrete & Foundations", icon: "Hammer", blurb: "Footings, slabs, walls, ICF, structural pours." },
  { slug: "site-prep",   title: "Site Prep & Grading", icon: "Truck", blurb: "Clearing, grading, compaction, base prep." },
  { slug: "decorative",  title: "Decorative & Stamped", icon: "LayoutGrid", blurb: "Stamped patios, exposed aggregate, finished surfaces." },
  { slug: "retaining-walls", title: "Retaining Walls", icon: "Trees", blurb: "Engineered retaining walls, lock-block, rock walls and hardscape." },
  { slug: "driveways", title: "Driveways, Paving & Surfacing", icon: "Route", blurb: "Driveways, paving, exposed aggregate and finished surfacing." },
  { slug: "drainage", title: "Drainage", subtitle: "Perimeter, Sewage & Sanitary", icon: "Droplets", blurb: "French drains, waterproofing, sump systems, code-compliant installs." },
];

export default function Home() {
  usePageTitle();
  return (
    <div data-testid="home-page">
      <HeroCarousel />

      {/* FEATURED BEFORE/AFTER */}
      <FeaturedTransformation />

      {/* STAT BAR */}
      <section className="bg-[#1A1F22] border-y border-white/10" data-testid="trust-strip">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-white">
          {[
            { big: "15+", small: "Years Breaking Ground" },
            { big: "100%", small: "Licensed · Insured · WCB" },
            { big: "2", small: "Regions — Island & Mainland" },
            { big: "48HR", small: "Quote Turnaround" },
          ].map((s) => (
            <div key={s.small} className="border-l-2 border-[#9CC4DB] pl-4" data-testid={`stat-${s.big.toLowerCase().replace(/[^a-z0-9]/g, "")}`}>
              <div className="font-display text-3xl md:text-4xl text-white leading-none">{s.big}</div>
              <div className="mt-1.5 text-xs md:text-sm tracking-[0.15em] uppercase text-white/60">{s.small}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="bg-[#2C3539] text-white" data-testid="home-services">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 pt-24 md:pt-32 pb-10">
          <div className="text-[#9CC4DB] text-xs tracking-[0.35em] font-bold uppercase mb-3">What We Do</div>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.95]">Seven service lines.<br/>One accountable crew.</h2>
          <p className="mt-5 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed">
            We self-perform excavation, concrete and site work end-to-end — so your project moves fast, stays on spec, and there's never any finger-pointing between subs.
          </p>
        </div>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 pb-24 md:pb-32 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {SERVICES.map((s) => {
            const Icon = ICONS[s.icon] || Hammer;
            return (
              <Link
                to={`/services/${s.slug}`}
                key={s.slug}
                data-testid={`home-service-${s.slug}`}
                className="bg-[#2C3539] hover:bg-[#2F5F80] group p-8 md:p-10 transition-colors flex flex-col gap-5 min-h-[220px]"
              >
                <div className="flex items-start justify-between">
                  <Icon size={36} className="text-[#9CC4DB] group-hover:text-white transition-colors" />
                  <ArrowRight size={20} className="text-white/40 group-hover:text-white -translate-x-1 group-hover:translate-x-0 transition-all" />
                </div>
                <div>
                  <div className="font-display text-2xl md:text-3xl uppercase tracking-tight">{s.title}</div>
                  {s.subtitle && <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#9CC4DB] group-hover:text-white/90 mt-1">{s.subtitle}</div>}
                  <p className="mt-2 text-sm md:text-base text-white/70 group-hover:text-white">{s.blurb}</p>
                </div>
              </Link>
            );
          })}
          <Link
            to="/contact"
            data-testid="home-service-cta-tile"
            className="bg-[#2F5F80] hover:bg-[#234B66] group p-8 md:p-10 transition-colors flex flex-col justify-between gap-5 min-h-[220px]"
          >
            <ArrowRight size={36} className="text-white" />
            <div>
              <div className="font-display text-2xl md:text-3xl uppercase tracking-tight text-white">Your Project Here</div>
              <p className="mt-2 text-sm md:text-base text-white/85">Free written estimates — tell us what you're building.</p>
            </div>
          </Link>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <HomeTestimonials />

      {/* ABOUT SLAB */}
      <section className="bg-white" data-testid="home-about">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[#2F5F80] text-xs tracking-[0.35em] font-bold uppercase mb-3">About AV Contracting</div>
            <h2 className="font-display text-5xl md:text-6xl uppercase leading-[0.95] text-[#23282B]">Built in BC.<br/>Backed by 15 years.</h2>
            <p className="mt-5 text-[#23282B]/80 text-base md:text-lg leading-relaxed">
              Amazing View Contracting is a Vancouver Island-based excavation and concrete crew working across British Columbia —
              from residential foundations and decorative concrete through heavy-civil and commercial site packages.
            </p>
            <p className="mt-4 text-[#23282B]/70 text-base leading-relaxed">
              Our promise is simple: precision groundwork, honest schedules, and a finish that holds up — for the lifetime of your build.
            </p>
            <Link to="/about" data-testid="home-about-cta" className="mt-8 inline-flex items-center gap-3 border-2 border-[#23282B] hover:bg-[#23282B] hover:text-white px-7 py-3 text-sm font-bold tracking-[0.15em] uppercase transition-colors">
              Our Story <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7 relative">
            <img
              src="https://customer-assets.emergentagent.com/job_av-job-command/artifacts/42b7t9n5_481261813_1077750730822566_2596512597816223226_n.jpg"
              alt="Concrete pour"
              className="w-full h-[420px] md:h-[520px] object-cover"
            />
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-[#23282B] text-white p-6 max-w-xs border-l-4 border-[#9CC4DB]">
              <div className="font-display text-3xl uppercase leading-none text-[#9CC4DB]">15+ YRS</div>
              <div className="text-sm mt-2 leading-relaxed text-white/85">of pouring foundations and breaking ground across BC.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A1F22] text-white av-grid" data-testid="home-final-cta">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-24 md:py-32 text-center">
          <div className="text-[#9CC4DB] text-xs tracking-[0.35em] font-bold uppercase mb-4">Ready to Break Ground?</div>
          <h3 className="font-display text-5xl md:text-7xl uppercase leading-[0.95]">Tell us about<br/>your project.</h3>
          <p className="mt-5 max-w-xl mx-auto text-white/70">Quotes within 48 hours. We work with homeowners, builders and developers across Vancouver Island and the Lower Mainland.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="final-cta-quote" className="bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-8 py-4 uppercase transition-colors">Request a Quote</Link>
            <Link to="/past-jobs" data-testid="final-cta-jobs" className="border-2 border-white/70 hover:border-[#9CC4DB] hover:text-[#9CC4DB] text-white text-sm font-bold tracking-[0.15em] px-8 py-4 uppercase transition-colors">View Past Jobs</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
